import { TFunction } from 'i18next'
import { ReactNode } from 'react'
import { z } from 'zod'

import { Badge } from '@/components/ui/badge'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { PhoneLink } from '@/components/ui/phone-link'
import { SelectOption } from '@/components/ui/select'
import { SortOrderSchema, TableColumn } from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants/ui'
import { Building } from '@/reducers/types'
import { formatDateOnly } from '@/utils/date'

import { CountBadgeTooltip } from '../shared/count-badge-tooltip'
import { PLACE_FEATURES } from './features/places-features.constants'
import { PlaceTab } from './place-details/place-details.types'
import { PlaceNameCell } from './place-name-cell'
import { PlaceStatusBadge } from './place-status-badge'
import { getBuildingPms, PlacePmsTypeSchema, PMS_LABELS, PMS_NONE } from './places.pms'
import { PlaceSortKey, PlaceSortKeySchema } from './places.sort-keys'
import { PlaceStatusFilter, PlaceStatusFilterSchema } from './places.status-filter'
import {
  getEnabledFeatureKeys,
  getKeylessProviderNames,
  getPlaceAddress,
  getPlaceStatus,
  getTimezoneLabel,
} from './places.utils'

export const PLACES_TABLE_ID = 'places-table'

export const placesTableFiltersSchema = z.object({
  feature: z.array(z.string()).optional(),
  fromDate: z.coerce.number().optional(),
  limit: z.coerce.number().optional(),
  pms: z.array(z.string()).optional(),
  search: z.string().optional(),
  skip: z.coerce.number().optional(),
  sortBy: PlaceSortKeySchema.optional(),
  sortOrder: SortOrderSchema.optional(),
  status: PlaceStatusFilterSchema.optional(),
  tags: z.array(z.string()).optional(),
  toDate: z.coerce.number().optional(),
})

export type PlacesTableFilters = z.infer<typeof placesTableFiltersSchema>

export const placesTableInitialFilters: PlacesTableFilters = {
  feature: undefined,
  fromDate: undefined,
  limit: DEFAULT_LIMIT,
  pms: undefined,
  search: '',
  skip: 0,
  sortBy: PlaceSortKey.name,
  sortOrder: 'asc',
  status: PlaceStatusFilter.all,
  tags: undefined,
  toDate: undefined,
}

export const getStatusFilterOptions = (
  t: TFunction<['organisation']>,
): SelectOption<PlaceStatusFilter>[] => [
  { label: t('organisation:places.table.filters.status.all'), value: PlaceStatusFilter.all },
  ...PlaceStatusFilterSchema.options
    .filter((status) => status !== PlaceStatusFilter.all)
    .map((status) => ({ label: t(`organisation:places.statuses.${status}`), value: status })),
]

export const getPmsFilterOptions = (t: TFunction<['organisation']>): SelectOption<string>[] => [
  ...PlacePmsTypeSchema.options.map((type) => ({ label: PMS_LABELS[type], value: type })),
  { label: t('organisation:places.table.filters.pms.none'), value: PMS_NONE },
]

export const getFeatureFilterOptions = (t: TFunction<['organisation']>): SelectOption<string>[] =>
  PLACE_FEATURES.filter(
    (feature) => feature.group !== 'account' && !('hidden' in feature && feature.hidden),
  )
    .map(({ key }) => ({ label: t(`organisation:places.building.toggles.${key}`), value: key }))
    .sort((a, b) => a.label.localeCompare(b.label))

const MutedText = ({ children }: { children: ReactNode }) => (
  <Typography color="black500" size="text-small" weight="medium" breakLines={1}>
    {children}
  </Typography>
)

export const getPlacesTableColumns = (t: TFunction<['organisation']>): TableColumn<Building>[] => [
  {
    id: PlaceSortKey.name,
    render: (building) => <PlaceNameCell building={building} />,
    sortKey: PlaceSortKey.name,
    title: t('organisation:places.table.columns.building'),
    width: '26rem',
  },
  {
    id: PlaceSortKey.address,
    render: (building) => {
      const { locality, street } = getPlaceAddress(building)
      return (
        <div>
          <Typography color="black800" size="text-small" weight="medium" breakLines={1}>
            {street || EMPTY_VALUE}
          </Typography>
          {locality && <MutedText>{locality}</MutedText>}
        </div>
      )
    },
    sortKey: PlaceSortKey.address,
    title: t('organisation:places.table.columns.address'),
    width: '26rem',
  },
  {
    id: PlaceSortKey.phone,
    render: (building) => (
      <MutedText>
        <PhoneLink phone={building.phone} />
      </MutedText>
    ),
    sortKey: PlaceSortKey.phone,
    title: t('organisation:places.table.columns.phone'),
    width: '14rem',
  },
  {
    id: PlaceSortKey.status,
    render: (building) => <PlaceStatusBadge status={getPlaceStatus(building)} />,
    sortKey: PlaceSortKey.status,
    title: t('organisation:places.table.columns.status'),
    width: '13rem',
  },
  {
    id: PlaceSortKey.pms,
    render: (building) => {
      const pms = getBuildingPms(building)
      return pms ? (
        <Badge color="blue" size="small" variant="light">
          {PMS_LABELS[pms]}
        </Badge>
      ) : (
        <MutedText>{EMPTY_VALUE}</MutedText>
      )
    },
    sortKey: PlaceSortKey.pms,
    title: t('organisation:places.table.columns.pms'),
    width: '13rem',
  },
  {
    id: PlaceSortKey.accessControl,
    render: (building) => {
      const providers = getKeylessProviderNames(building)
      return providers.length ? (
        <CountBadgeTooltip
          badgeLabel={t('organisation:places.table.providersCount', { count: providers.length })}
          items={providers}
          moreLabel={(count) => t('organisation:places.table.moreItems', { count })}
        />
      ) : (
        <MutedText>{EMPTY_VALUE}</MutedText>
      )
    },
    sortKey: PlaceSortKey.accessControl,
    title: t('organisation:places.table.columns.accessControl'),
    width: '15rem',
  },
  {
    id: PlaceSortKey.features,
    render: (building) => {
      const features = getEnabledFeatureKeys(building)
      return features.length ? (
        <CountBadgeTooltip
          badgeLabel={t('organisation:places.table.featuresCount', { count: features.length })}
          items={features.map((key) => t(`organisation:places.building.toggles.${key}`))}
          moreLabel={(count) => t('organisation:places.table.moreItems', { count })}
          moreTo={{
            params: { buildingId: String(building.id) },
            path: '/dashboard/organisation/places/:buildingId',
            queryParams: { tab: PlaceTab.features },
          }}
        />
      ) : (
        <MutedText>{EMPTY_VALUE}</MutedText>
      )
    },
    sortKey: PlaceSortKey.features,
    title: t('organisation:places.table.columns.features'),
    width: '15rem',
  },
  {
    id: PlaceSortKey.timezone,
    render: (building) => (
      <MutedText>{getTimezoneLabel(building.timezone) ?? EMPTY_VALUE}</MutedText>
    ),
    sortKey: PlaceSortKey.timezone,
    title: t('organisation:places.table.columns.timezone'),
    width: '11rem',
  },
  {
    id: PlaceSortKey.createdAt,
    render: (building) => (
      <MutedText>
        {building.createdAt ? formatDateOnly(new Date(building.createdAt)) : EMPTY_VALUE}
      </MutedText>
    ),
    sortKey: PlaceSortKey.createdAt,
    title: t('organisation:places.table.columns.createdAt'),
    width: '13rem',
  },
]
